<script lang="ts" context="module">
  import { safe_not_equal } from "svelte/internal";
  import type { ControlValidator, ValidatorResult } from "./validators";

  export class ArrayControl<TControl extends Control<any>, T = TControl extends Control<infer T> ? T : never> extends FormControl<T[]> {
    constructor(name: string, validators?: ControlValidator[], value?: T[]) {
      super(name, validators);
      if (value !== undefined) this.setValue(value, false);
    }

    array: ControlsCollection<TControl, T> = new ControlsCollection<TControl, T>(this);
    createControl?: (value: T) => TControl;

    get value() {
      const array = [];
      if (this.keepValues) {
        for (const key of Object.keys(this.values)) {
          array[key] = this.values[key];
        }
      }
      for (let i = 0; i < this.array.length; i++) {
        array[i] = this.array[i].value;
      }
      return (array.length ? array : undefined) as T[];
    }

    /** Sets the value of this form control and emits value changed events */
    set value(value: T[]) {
      super.value = value;
    }

    setValue(value: T[], emitEvents: boolean, forceValidation: boolean = false) {
      const obj = Array.isArray(value) ? value : [];
      for (let i = 0; i < obj.length; i++) {
        const control = this.array[i];
        if (control) control.setValue(obj[i], emitEvents, forceValidation);
        else this.array.add(obj[i]);
      }
      for (const control of this.array.slice(obj.length)) {
        this.array.remove(control);
      }
      if (emitEvents) this.validate(forceValidation);
    }

    /** Overwrites the base function to act like a 'simple' control */
    patchValue(value: Partial<T[]>, emitEvents: boolean = true) {
      this.setValue(value, emitEvents);
    }

    registerControl(control: Control) {
      super.registerControl(control);

      if (typeof this.values[control.name] === "undefined" || safe_not_equal(control.value, this.values[control.name])) {
        this.valueChanges.set(this);
      }

      this.validate();
    }

    removeControl(control: Control) {
      super.removeControl(control);

      if (!this.array.includes(control as any)) {
        delete this.values[control.name]; // clear values, becasue user intended to delete this control
        this.valueChanges.set(this);
      }

      this.validate();
    }
  }

  export class ControlsCollection<TControl extends Control<T>, T> extends Array<TControl> {
    constructor(private form: ArrayControl<TControl, T>) {
      super();
    }

    add(value?: T | TControl) {
      let control: TControl;
      if (value instanceof Control) {
        control = value;
      } else if (this.form.createControl) {
        control = this.form.createControl(value);
      } else {
        throw new Error("Cannot create a control for a value because 'createControl' callback was not set. value: " + JSON.stringify(value));
      }

      control.name = this.length as any;
      delete this.values[control.name]; // clear values, because user intended to add a new empty control
      this.push(control);
      this.form.update();
    }

    remove(control: TControl) {
      const index = this.indexOf(control);
      if (index !== -1) {
        // clear all values!
        for (let i = 0; i < this.length; i++) {
          delete this.form.values[i];
        }

        // remove control
        this.splice(index, 1);

        // update control names to reflect new index order
        for (let i = 0; i < this.length; i++) {
          const ctrl = this[i];
          ctrl.name = i as any;
        }
      }

      this.form.update();
    }
  }
</script>

<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { Control, FormControl, setupControl } from "./controls";
  import { Validator } from "./validators";

  const dispatch = createEventDispatcher();

  type TArrayControl = $$Generic<ArrayControl<any>>;
  type TControl = $$Generic<TArrayControl extends ArrayControl<infer T> ? T : never>;
  type T = $$Generic<TControl extends Control<infer T> ? T : never>;

  interface $$Slots {
    default: {
      array: ControlsCollection<TControl, T>;
      errors: ValidatorResult[];
      control: TArrayControl;
    };
  }

  // either set name and validators
  export let name: string = null;
  export let required: boolean = false;
  export let validators: ControlValidator[] = [];
  export let value: any[] = undefined;
  // ... or bind a control
  export let control: TArrayControl = null;
  export let standalone: boolean = false;
  export let createControl: (value: T) => TControl = defaultCreateControl;

  function defaultCreateControl(value: T) {
    return new Control("item", null, value) as TControl;
  }

  const changes = setupControl<TArrayControl>(ArrayControl as any, {
    name,
    validators: [...validators, ...(required ? [Validator.required] : [])],
    value,
    control,
    standalone,
    submitEvent: (ctrl) => dispatch("submit", ctrl),
    configure: (ctrl) => {
      if (!ctrl.createControl || (ctrl.createControl && createControl !== defaultCreateControl)) {
        ctrl.createControl = createControl;
      }
    },
    register: true,
  });

  $: ctrl = $changes;

  let oldValue = value;
  $: if (value !== oldValue) {
    oldValue = value;
    ctrl.value = value;
  }
</script>

<slot array={ctrl.array} errors={ctrl.errors} control={ctrl} />
